/**
 * Learner formative progress detail (Phase 2D.5).
 * No overall academic score / official marks.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Select } from '../../components/ui/Select';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { useTutorTenantLabels } from '../../modules/tutor/hooks/useTutorTenantLabels';
import {
  getMalayFormativeActivityDetail,
  getMalayUnit5Progress,
  type FormativeActivityDetail,
  type MasteryLevel,
  type Unit5ProgressSummary,
} from '../../modules/tutor/api/malayFormativeProgress.client';
import { ROUTES } from '../../utils/constants';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';
import { FormativeSkillDevelopmentSection } from '../../components/teacher/canDoProgress/FormativeSkillDevelopmentSection';

const ACTIVITIES = [
  { value: 'malay_unit_5', labelKey: 'dashboard.malayFormative.activities.unit5' },
  {
    value: 'malay_jendela_seni_reading',
    labelKey: 'dashboard.malayFormative.activities.reading',
  },
  {
    value: 'malay_narrative_learning_tutor',
    labelKey: 'dashboard.malayFormative.activities.narrative',
  },
] as const;

export default function MalayLearningProgressPage() {
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled, loading: featureLoading } = useFeatureAccess();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const [activityKey, setActivityKey] = useState<string>(ACTIVITIES[0].value);
  const [detail, setDetail] = useState<FormativeActivityDetail | null>(null);
  const [unit5Progress, setUnit5Progress] = useState<Unit5ProgressSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const allowed = labels.isMalay && isSchoolFeatureEnabled('ai_tutor');

  useEffect(() => {
    if (featureLoading || labelsLoading || !allowed) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getMalayFormativeActivityDetail(activityKey).catch(() => null),
      activityKey === 'malay_unit_5' ? getMalayUnit5Progress().catch(() => null) : Promise.resolve(null),
    ])
      .then(([d, u5]) => {
        if (cancelled) return;
        if (d) setDetail(d);
        else {
          setError(t('dashboard.malayFormative.loadError'));
          setDetail(null);
        }
        setUnit5Progress(u5);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err) || t('dashboard.malayFormative.loadError'));
          setDetail(null);
          setUnit5Progress(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activityKey, allowed, featureLoading, labelsLoading, t]);

  const options = useMemo(
    () => ACTIVITIES.map((a) => ({ value: a.value, label: t(a.labelKey) })),
    [t],
  );

  if (!featureLoading && !labelsLoading && !labels.isMalay) {
    return <Navigate to={ROUTES.STUDENT_DASHBOARD} replace />;
  }

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-app-text">
            {t('dashboard.malayFormative.detailTitle')}
          </h1>
          <p className="text-sm text-app-text-muted">
            {t('dashboard.malayFormative.detailSubtitle')}
          </p>
        </div>
        <Link to={ROUTES.STUDENT_DASHBOARD} className="text-sm font-semibold text-app-primary">
          {t('dashboard.malayFormative.backHome')}
        </Link>
      </div>

      <Card className="mb-4 border-app-border bg-app-surface">
        <CardContent className="pt-4">
          <label className="mb-1 block text-xs font-semibold text-app-text-muted">
            {t('dashboard.malayFormative.filterActivity')}
          </label>
          <Select
            value={activityKey}
            onChange={(e) => setActivityKey(e.target.value)}
            options={options}
          />
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-app-text-muted" role="status">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          {t('dashboard.malayFormative.loading')}
        </div>
      ) : error ? (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          {error}
        </p>
      ) : (
        <>
          <Card className="mb-4 border-app-border bg-app-surface">
            <CardHeader>
              <CardTitle>{t('dashboard.malayFormative.progressStatus')}</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {detail?.progress ? (
                <p>
                  {t('dashboard.malayFormative.completionOf', {
                    completed: Number(detail.progress.completed_units || 0),
                    total: Number(detail.progress.total_units || 0),
                  })}{' '}
                  · {String(detail.progress.status || 'in_progress')}
                </p>
              ) : (
                <p className="text-app-text-muted">{t('dashboard.malayFormative.empty')}</p>
              )}
            </CardContent>
          </Card>

          {activityKey === 'malay_unit_5' && unit5Progress?.lessons?.length ? (
            <Card className="mb-4 border-app-border bg-app-surface">
              <CardHeader>
                <CardTitle>Unit 5 — Lesson assessment status</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {unit5Progress.lessons.map((lesson) => (
                    <li
                      key={lesson.topic_id}
                      className="rounded-xl border border-app-border px-3 py-2 text-sm"
                      data-testid={`malay-unit5-lesson-${lesson.topic_id}`}
                    >
                      <div className="font-semibold">{lesson.title_ms}</div>
                      <p className="mt-1 text-xs text-app-text-muted">
                        Teaching: {lesson.teaching_progress_percent ?? 0}%
                      </p>
                      {lesson.assessment_status === 'completed' ? (
                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                          Assessment: Completed
                          {lesson.assessment_score != null ? ` · Score ${lesson.assessment_score}/100` : ''}
                          {lesson.assessment_mastery_display_ms
                            ? ` · ${lesson.assessment_mastery_display_ms}`
                            : ''}
                        </p>
                      ) : lesson.assessment_status === 'in_progress' ? (
                        <p className="text-xs font-semibold text-violet-700 dark:text-violet-300">
                          Assessment: In Progress {lesson.assessment_progress_percent ?? 0}%
                          {lesson.assessment_targets_total
                            ? ` (${lesson.assessment_targets_completed ?? 0}/${lesson.assessment_targets_total} sasaran)`
                            : ''}
                        </p>
                      ) : lesson.assessment_status === 'ready' ? (
                        <p className="text-xs text-app-text-muted">Assessment: Ready</p>
                      ) : (
                        <p className="text-xs text-app-text-muted">Assessment: Locked</p>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ) : null}

          <Card className="mb-4 border-app-border bg-app-surface">
            <CardHeader className="pb-2">
              <CardTitle>{t('dashboard.malayFormative.skills')}</CardTitle>
            </CardHeader>
            <CardContent>
              <FormativeSkillDevelopmentSection
                activityKey={activityKey}
                detail={detail}
                showSummary={true}
              />
            </CardContent>
          </Card>

          <Card className="border-app-border bg-app-surface">
            <CardHeader>
              <CardTitle>{t('dashboard.malayFormative.recentFeedback')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {(detail?.feedback_history || []).length === 0 ? (
                <p className="text-app-text-muted">{t('dashboard.malayFormative.empty')}</p>
              ) : (
                (detail?.feedback_history || []).map((fb, idx) => (
                  <article
                    key={idx}
                    className="rounded-xl border border-app-border px-3 py-2"
                  >
                    <p>
                      <span className="font-semibold">
                        {t('dashboard.malayFormative.next')}:
                      </span>{' '}
                      {String(
                        (fb.recommended_next_step as { message?: string } | undefined)
                          ?.message || '',
                      )}
                    </p>
                  </article>
                ))
              )}
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
